import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ServerResponse, TreetopPropertiesProvider } from 'shared/util-common';

import { Guideline, GuidelineDto, PaginationWrapperDto, SignedGuideline, SmartbookGetOptions } from '@treetop/shared/model';

import { mapDtoToGuideline } from './guideline-page-domain/util/guideline.service.util';
import { MessagesService } from './messages.service';
import { createSmartbookParams } from './smartbook-http.service.util';

const defaultSmartbookFilter = {
  searchText: '',
  guidelineStates: [],
  organizationIds: [],
  owners: [],
  authors: [],
  showSharedGuidelines: true,
  showSoonToExpire: false,
  showExpired: false,
};

type SignedGuidelineDto = {
  uniqueId: string;
  version: string;
  guideline: string;
  signature: string;
  signedAt: Date;
  title: string;
};

@Injectable({
  providedIn: 'root',
})
export class SmartbookHttpService {
  private http = inject(HttpClient);
  private messages = inject(MessagesService);

  private baseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api`;

  public get(page: number, pageSize: number, options?: SmartbookGetOptions): Observable<ServerResponse<Guideline>> {
    const _options: SmartbookGetOptions = {
      ...options,
      filter: {
        ...defaultSmartbookFilter,
        ...options?.filter,
      },
      includeUpdatedSinceLastLoginInfo: true,
    };

    const params = createSmartbookParams({ page, itemsPerPage: pageSize }, _options);

    return this.http.get<PaginationWrapperDto<GuidelineDto>>(`${this.baseUrl}/smart-book`, { params }).pipe(
      map((response) => ({
        list: response.list.map(mapDtoToGuideline),
        page: response.page,
        itemsPerPage: response.itemsPerPage,
        maxPages: response.maxPage,
        maxElements: response.maxElements,
      })),
      catchError((err) => {
        this.messages.showError('CouldNotLoadSmartbook');
        return throwError(() => err);
      }),
    );
  }

  public getSmartbookByUniqueId(uniqueId: string): Observable<Guideline> {
    return this.http.get<GuidelineDto>(`${this.baseUrl}/smart-book/${uniqueId}`).pipe(map(mapDtoToGuideline));
  }

  public getSmartbookWithSignatureCheck(uniqueIds: string[]): Observable<SignedGuideline[]> {
    return this.http.post<SignedGuidelineDto[]>(`${this.baseUrl}/smart-book/with-signatures`, uniqueIds);
  }

  public getPublicKey(): Observable<string> {
    return this.http.get(`${this.baseUrl}/smart-book/public-key`, {
      responseType: 'text',
    });
  }

  public getSmartbookHasUpdateMessage(uniqueId: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseUrl}/smart-book/${uniqueId}/has-update-message`);
  }

  public setUpdateMessageAsViewed(uniqueId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/smart-book/${uniqueId}/set-update-message-as-viewed`, {});
  }

  public getGuidelinePreviewByUniqueId(uniqueId: string): Observable<Guideline> {
    return this.http.get<GuidelineDto>(`${this.baseUrl}/smart-book/preview/${uniqueId}`).pipe(map(mapDtoToGuideline));
  }

  public getSmartbookPreviewByUniqueId(uniqueId: string, version?: string): Observable<Guideline> {
    const url = version
      ? `${this.baseUrl}/smart-book/preview/${uniqueId}?version=${version}`
      : `${this.baseUrl}/smart-book/preview/${uniqueId}`;

    return this.http.get<GuidelineDto>(url).pipe(
      catchError((err) => {
        this.messages.showError(`Could not load smartbook`);
        return throwError(err);
      }),
      map(mapDtoToGuideline),
    );
  }

  public getSmartbookPublicPreviewByUniqueId(uniqueId: string): Observable<Guideline> {
    return this.http.get<GuidelineDto>(`${this.baseUrl}/smart-book/public-preview/${uniqueId}`).pipe(map(mapDtoToGuideline));
  }
}
